/* eslint-disable lingui/no-unlocalized-strings */

const SQL_RESULTS_CHART_RESOURCE_URI_PREFIX = 'ui://channel99/sql-results-chart-';
const SQL_RESULTS_CHART_RESOURCE_URI_SUFFIX = '/app.html';
const BUILD_ID_PATTERN = /^[a-zA-Z0-9._-]+$/;

export const parseBuildIdFromResourceUri = (resourceUri: string): string | null => {
  if (
    !resourceUri.startsWith(SQL_RESULTS_CHART_RESOURCE_URI_PREFIX) ||
    !resourceUri.endsWith(SQL_RESULTS_CHART_RESOURCE_URI_SUFFIX)
  ) {
    return null;
  }

  const buildId = resourceUri.slice(
    SQL_RESULTS_CHART_RESOURCE_URI_PREFIX.length,
    -SQL_RESULTS_CHART_RESOURCE_URI_SUFFIX.length,
  );

  if (buildId.length === 0 || !BUILD_ID_PATTERN.test(buildId)) {
    return null;
  }

  return buildId;
};

export const getMcpServerHttpOriginFromWsUrl = (wsUrl: string): string | null => {
  try {
    const url = new URL(wsUrl);

    if (url.protocol === 'ws:') {
      return `http://${url.host}`;
    }

    if (url.protocol === 'wss:') {
      return `https://${url.host}`;
    }

    return null;
  } catch {
    return null;
  }
};

export const getAppBundleUrl = (resourceUri: string, mcpServerOrigin: string): string | null => {
  const buildId = parseBuildIdFromResourceUri(resourceUri);

  if (!buildId) {
    return null;
  }

  return `${mcpServerOrigin}/mcp-apps/sql-results-chart/${buildId}/app.html`;
};

export type AppBundleFetchResult =
  | { status: 'success'; html: string }
  | { status: 'not_found' }
  | { status: 'auth_error' }
  | { status: 'error' };

const bundleCache = new Map<string, string>();
const bundleStatusCache = new Map<string, AppBundleFetchResult['status']>();

export const getCachedAppBundleHtml = (resourceUri: string): string | undefined =>
  bundleCache.get(resourceUri);

export const getAppBundleFetchStatus = (
  resourceUri: string,
): AppBundleFetchResult['status'] | undefined => bundleStatusCache.get(resourceUri);

export const clearAppBundleCache = (): void => {
  bundleCache.clear();
  bundleStatusCache.clear();
};

type FetchAppBundleHtmlParams = {
  resourceUri: string;
  token: string | undefined;
  mcpServerWsUrl: string;
  fetchFn?: typeof fetch;
};

const cacheBundleResult = (resourceUri: string, result: AppBundleFetchResult): AppBundleFetchResult => {
  bundleStatusCache.set(resourceUri, result.status);

  if (result.status === 'success') {
    bundleCache.set(resourceUri, result.html);
  }

  return result;
};

export const fetchAppBundleHtml = async ({
  resourceUri,
  token,
  mcpServerWsUrl,
  fetchFn = fetch,
}: FetchAppBundleHtmlParams): Promise<AppBundleFetchResult> => {
  if (bundleCache.has(resourceUri)) {
    return cacheBundleResult(resourceUri, {
      status: 'success',
      html: bundleCache.get(resourceUri)!,
    });
  }

  const cachedStatus = bundleStatusCache.get(resourceUri);
  if (cachedStatus && cachedStatus !== 'success') {
    return { status: cachedStatus };
  }

  const mcpServerOrigin = getMcpServerHttpOriginFromWsUrl(mcpServerWsUrl);
  const bundleUrl = mcpServerOrigin ? getAppBundleUrl(resourceUri, mcpServerOrigin) : null;

  if (!bundleUrl || !token) {
    return { status: 'error' };
  }

  const response = await fetchFn(bundleUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 404) {
    return cacheBundleResult(resourceUri, { status: 'not_found' });
  }

  if (response.status === 401 || response.status === 403) {
    return cacheBundleResult(resourceUri, { status: 'auth_error' });
  }

  if (!response.ok) {
    return cacheBundleResult(resourceUri, { status: 'error' });
  }

  const html = await response.text();

  return cacheBundleResult(resourceUri, { status: 'success', html });
};
